import apiClient from './apiClient';
import { getAuthToken } from '../utils/storage';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.risman.pro/api';
const unwrapApiData = (data) => data?.data || data?.Data || data;

const normalizeRun = (raw) => ({
  id: raw.id ?? raw.Id,
  projectId: raw.projectId ?? raw.ProjectId,
  chatId: raw.chatId ?? raw.ChatId,
  mode: raw.mode ?? raw.Mode,
  status: raw.status ?? raw.Status,
  provider: raw.provider ?? raw.Provider,
  model: raw.model ?? raw.Model,
  prompt: raw.prompt ?? raw.Prompt,
  planJson: raw.planJson ?? raw.PlanJson,
  finalMessage: raw.finalMessage ?? raw.FinalMessage,
  errorMessage: raw.errorMessage ?? raw.ErrorMessage,
  currentStep: raw.currentStep ?? raw.CurrentStep,
  maxSteps: raw.maxSteps ?? raw.MaxSteps,
  promptTokens: raw.promptTokens ?? raw.PromptTokens,
  completionTokens: raw.completionTokens ?? raw.CompletionTokens,
  createdAt: raw.createdAt ?? raw.CreatedAt,
  startedAtUtc: raw.startedAtUtc ?? raw.StartedAtUtc,
  completedAtUtc: raw.completedAtUtc ?? raw.CompletedAtUtc
});

const normalizeEvent = (raw) => ({
  id: raw.id ?? raw.Id,
  runId: raw.runId ?? raw.RunId,
  seq: raw.seq ?? raw.Seq,
  type: raw.type ?? raw.Type,
  payloadJson: raw.payloadJson ?? raw.PayloadJson,
  createdAt: raw.createdAt ?? raw.CreatedAt
});

export const startAgentRunApi = async (payload) => {
  const { data } = await apiClient.post('/agent/runs', {
    chatId: payload.chatId,
    projectId: payload.projectId,
    prompt: payload.prompt,
    mode: payload.mode,
    provider: payload.provider,
    model: payload.model,
    maxSteps: payload.maxSteps
  });
  return normalizeRun(unwrapApiData(data) ?? data);
};

export const getAgentRunApi = async (runId) => {
  const { data } = await apiClient.get(`/agent/runs/${runId}`);
  return normalizeRun(unwrapApiData(data) ?? data);
};

export const getAgentEventsApi = async (runId, afterSeq = 0) => {
  const { data } = await apiClient.get(`/agent/runs/${runId}/events`, { params: { afterSeq } });
  const list = unwrapApiData(data) ?? data ?? [];
  return (Array.isArray(list) ? list : []).map(normalizeEvent);
};

export const pauseAgentRunApi = (runId) => apiClient.post(`/agent/runs/${runId}/pause`);
export const resumeAgentRunApi = (runId) => apiClient.post(`/agent/runs/${runId}/resume`);
export const stopAgentRunApi = (runId) => apiClient.post(`/agent/runs/${runId}/stop`);
export const approveAgentPlanApi = (runId, editedPlanJson) =>
  apiClient.post(`/agent/runs/${runId}/approve-plan`, { editedPlanJson: editedPlanJson || null });

export const streamAgentEventsApi = async ({ runId, afterSeq = 0, onEvent, signal }) => {
  const token = getAuthToken();
  const url = `${API_BASE}/agent/runs/${runId}/events/stream?afterSeq=${afterSeq}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    signal
  });

  if (!response.ok || !response.body) {
    throw new Error(`Agent stream failed (${response.status})`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split('\n\n');
    buffer = chunks.pop() || '';

    for (const chunk of chunks) {
      const lines = chunk.split('\n');
      let eventType = 'message';
      let data = '';
      for (const line of lines) {
        if (line.startsWith('event:')) eventType = line.slice(6).trim();
        if (line.startsWith('data:')) data += line.slice(5).trim();
      }
      if (!data || data === '[DONE]') continue;
      try {
        const parsed = JSON.parse(data);
        onEvent?.(normalizeEvent({ ...parsed, type: parsed.type || eventType }));
      } catch {
        onEvent?.({ type: eventType, payloadJson: data, seq: 0 });
      }
    }
  }
};

export const listSkillsApi = async () => {
  const { data } = await apiClient.get('/skills');
  const list = unwrapApiData(data) ?? data ?? [];
  return (Array.isArray(list) ? list : []).map((s) => ({
    id: s.id ?? s.Id,
    key: s.key ?? s.Key,
    title: s.title ?? s.Title,
    description: s.description ?? s.Description,
    triggers: s.triggers ?? s.Triggers,
    bodyMarkdown: s.bodyMarkdown ?? s.BodyMarkdown,
    kind: s.kind ?? s.Kind,
    ownerUserId: s.ownerUserId ?? s.OwnerUserId
  }));
};

export const createSkillApi = async (payload) => {
  const { data } = await apiClient.post('/skills', payload);
  return unwrapApiData(data) ?? data;
};

export const deleteSkillApi = (skillId) => apiClient.delete(`/skills/${skillId}`);

export const listSecretsApi = async (projectId) => {
  const { data } = await apiClient.get(`/projects/${projectId}/secrets`);
  const list = unwrapApiData(data) ?? data ?? [];
  return (Array.isArray(list) ? list : []).map((s) => ({
    id: s.id ?? s.Id,
    key: s.key ?? s.Key,
    maskedValue: s.maskedValue ?? s.MaskedValue,
    updatedAt: s.updatedAt ?? s.UpdatedAt
  }));
};

export const upsertSecretApi = async (projectId, payload) => {
  const { data } = await apiClient.put(`/projects/${projectId}/secrets`, payload);
  return unwrapApiData(data) ?? data;
};

export const deleteSecretApi = (projectId, secretId) =>
  apiClient.delete(`/projects/${projectId}/secrets/${secretId}`);

export const downloadProjectZipApi = async (projectId) => {
  const response = await apiClient.get(`/projects/${projectId}/source-files/download.zip`, {
    responseType: 'blob'
  });
  return response.data;
};
